/**
 * Composer skill-offer DOM helpers.
 * Preview uses the CSS Highlight API so the caret is not moved.
 * Accept replaces the keyword range with the existing slash chip.
 */

import { isOfferKeywordHit } from '@/lib/skill-keyword-span'

const PREVIEW_NAME = 'maater-skill-offer'
const PREVIEW_HOT_NAME = 'maater-skill-offer-hot'

const previewTimers = new Map<HTMLElement, ReturnType<typeof setInterval>>()

function highlightsApi(): HighlightRegistry | null {
  if (typeof CSS === 'undefined' || !CSS.highlights || typeof Highlight === 'undefined') {
    return null
  }
  return CSS.highlights
}

export function findKeywordRange(root: HTMLElement, keyword: string): Range | null {
  const needle = keyword.trim().toLowerCase()
  if (!needle) return null
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    const parent = (node as Text).parentElement
    if (parent?.closest('[data-reference-type="chip"]')) {
      node = walker.nextNode()
      continue
    }
    const text = node.textContent || ''
    const lower = text.toLowerCase()
    let from = 0
    while (from <= lower.length - needle.length) {
      const start = lower.indexOf(needle, from)
      if (start < 0) break
      const end = start + needle.length
      if (isOfferKeywordHit(text, start, end)) {
        const range = document.createRange()
        range.setStart(node, start)
        range.setEnd(node, end)
        return range
      }
      from = start + 1
    }
    node = walker.nextNode()
  }
  return null
}

export function clearSkillOfferPreview(root?: HTMLElement): void {
  if (root) {
    const timer = previewTimers.get(root)
    if (timer) clearInterval(timer)
    previewTimers.delete(root)
  } else {
    for (const timer of previewTimers.values()) clearInterval(timer)
    previewTimers.clear()
  }
  const api = highlightsApi()
  api?.delete(PREVIEW_NAME)
  api?.delete(PREVIEW_HOT_NAME)
}

export function showSkillOfferPreview(root: HTMLElement, keyword: string): void {
  clearSkillOfferPreview(root)
  const api = highlightsApi()
  if (!api) return
  const range = findKeywordRange(root, keyword)
  if (!range) return

  const paint = (hot: boolean) => {
    api.delete(PREVIEW_NAME)
    api.delete(PREVIEW_HOT_NAME)
    api.set(hot ? PREVIEW_HOT_NAME : PREVIEW_NAME, new Highlight(range))
  }

  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  paint(false)
  if (reduce) return

  let hot = false
  previewTimers.set(
    root,
    setInterval(() => {
      hot = !hot
      paint(hot)
    }, 700),
  )
}

export function replaceKeywordWithSkillChip(
  root: HTMLElement,
  keyword: string,
  chipEl: HTMLElement,
): boolean {
  clearSkillOfferPreview(root)
  const range = findKeywordRange(root, keyword)
  if (!range) return false
  const replaced = range.toString()
  range.deleteContents()
  chipEl.setAttribute('data-replaced-text', replaced)
  range.insertNode(chipEl)
  return true
}

/** Put the replaced word back when the slash badge X is clicked. */
export function restoreReplacedSkillText(chipEl: Element): void {
  const replaced = chipEl.getAttribute('data-replaced-text')
  if (replaced) {
    chipEl.replaceWith(document.createTextNode(replaced))
    return
  }
  chipEl.remove()
}
